/**
 * The colour of a ticket's status tag, in ONE place for the whole ticket
 * area. It was the same switch in the list, the project page and the gantt's
 * backlog table - three literals that had already started to drift
 * ("in-progress" was primary in one and default in another).
 */
export const TICKET_STATUS_TAG_TYPES = {
    new: 'danger',
    // A question somebody is waiting on, like the two snoozed states.
    'estimate-effort': 'warning',
    'in-progress': 'primary',
    pending: 'warning',
    'on-hold': 'warning',
    completed: 'success',
    // A dead end, not a success and not a warning: the work stopped.
    'not-reproducible': 'info',
    closed: 'info',
};

export const ticketStatusTagType = (status) => TICKET_STATUS_TAG_TYPES[status] || 'info';

/**
 * Every status there is, in the order the backend lists them
 * (`FilterOptions::statuses()`), so a select, a filter dropdown, a legend and
 * a status group all read in the same order as the ticket's own form.
 *
 * Keep this the ONE list. The gantt carried its own copy and simply never
 * learned about `not-reproducible`: the status was missing from its filter and
 * its legend, and a bar in that status drew with no fill at all.
 */
/**
 * Asked for rather than worked on. Named because two rules key off it: the
 * server raises the priority on the way in, and the ticket form hands the
 * ticket back to its creator the moment the estimate is typed.
 */
export const TICKET_STATUS_ESTIMATE_EFFORT = 'estimate-effort';

/**
 * The step out of "new" into "in progress" is taking a ticket on: the server
 * makes whoever does it the assignee when nobody is (Ticket::changeStatus),
 * and the ticket form shows that before the save.
 */
export const TICKET_STATUS_NEW = 'new';
export const TICKET_STATUS_IN_PROGRESS = 'in-progress';

export const TICKET_STATUS_KEYS = [
    'new',
    'estimate-effort',
    'in-progress',
    'pending',
    'on-hold',
    'completed',
    'not-reproducible',
    'closed',
];

/**
 * What a status looks like on the gantt canvas, so the legend, the bars and
 * the ghost the cursor carries cannot drift apart. `not-reproducible` takes
 * the muted grey this area already uses for secondary text, which keeps it
 * apart from `closed`'s lighter grey while saying the same thing its `info`
 * tag does: the work stopped.
 */
export const TICKET_STATUS_COLORS = {
    new: '#F56C6C',
    // The one hue the chart does not already spend: red, blue, orange, green
    // and two greys are taken, so a violet is what keeps the legend readable.
    'estimate-effort': '#9B6CF5',
    'in-progress': '#409EFF',
    pending: '#E6A23C',
    'on-hold': '#E6A23C',
    completed: '#67C23A',
    'not-reproducible': '#909399',
    closed: '#C0C4CC',
};

// Ticket::FINISHED_STATUSES: done, whatever its dates say.
const TICKET_FINISHED_STATUSES = ['completed', 'not-reproducible', 'closed'];

export const isFinishedTicket = (ticket) => TICKET_FINISHED_STATUSES.indexOf(ticket.status) !== -1;

// Still being worked on: what the timer dialogs offer to book time on.
export const TICKET_OPEN_STATUS_KEYS = TICKET_STATUS_KEYS.filter((status) => TICKET_FINISHED_STATUSES.indexOf(status) === -1);

// What a person may set: closed is system-set by the status timers.
export const TICKET_CHOOSABLE_STATUS_KEYS = TICKET_STATUS_KEYS.filter((status) => status !== 'closed');
